import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * The breakpoint ladder.
 *
 * The CSS in this app reaches for thirteen different widths (380, 400, 420, 480,
 * 520, 600, 640, 768, 860, 900, 960, 1024, 1100), which means a layout can change
 * at a width no other layout agrees is interesting. These six are the ones that
 * carry real weight; the rest are one-offs. New code uses these, and files being
 * edited for other reasons get normalised onto them as they are touched.
 */
export const BREAKPOINTS = {
  xs: 400,
  sm: 480,
  md: 640,
  lg: 768,
  xl: 960,
  xxl: 1100,
} as const;

const subscribeToNothing = () => () => {};

/**
 * Whether a media query currently matches, without breaking server rendering.
 *
 * `useSyncExternalStore` rather than the two obvious alternatives, both of which
 * are wrong here in ways that show up on a real phone:
 *
 *  - `useState(() => matchMedia(q).matches)` reads the window during render, so
 *    the server renders `false` and the client renders `true` — a hydration
 *    mismatch that React resolves by keeping the server's markup, leaving a
 *    desktop tree on a phone until something else happens to re-render;
 *  - `useEffect` + `setState` renders the wrong tree once and then corrects it,
 *    which is a visible flash and, where the value picks the component, a wasted
 *    mount.
 *
 * `getServerSnapshot` returning `false` makes the server and the first client
 * render agree by construction; the subscription then updates immediately after.
 * So a mobile-only branch must be written as a progressive enhancement, never as
 * the only way to reach something.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === "undefined" || !window.matchMedia) return () => {};
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  }, [query]);

  return useSyncExternalStore(
    typeof window === "undefined" ? subscribeToNothing : subscribe,
    getSnapshot,
    () => false,
  );
}

/**
 * A coarse pointer — a finger, near enough.
 *
 * Deliberately not `"ontouchstart" in window`: a touchscreen laptop reports touch
 * support and still has a mouse, and taking its hover affordances away makes it
 * worse. `pointer: coarse` describes the *primary* pointer, which is the thing
 * worth branching on.
 */
export function useIsTouch(): boolean {
  return useMediaQuery("(pointer: coarse)");
}

/** A narrow viewport. Phone-sized, and the tablet portrait that behaves like one. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.lg}px)`);
}

/**
 * Fit the chat to the part of the screen the keyboard leaves visible.
 *
 * This used to pad the body by half a screen on focus and smooth-scroll the input
 * to the middle, then scroll back to the top on blur. On an iPhone that read as
 * the whole page lurching: the header slid away, the input floated mid-screen
 * over a band of empty space, and because the document itself could scroll, a
 * swipe up through a long answer ran on into Safari's pull-to-refresh and threw
 * the conversation away.
 *
 * Nothing scrolls here except the message list. The chat is sized from its own
 * top edge to the bottom of the visual viewport, which is the top of the
 * keyboard when one is open, so the input always sits just above it. iOS still
 * scrolls the page to reveal a focused field even when the page does not need to
 * move; that scroll is undone, since the field is already in view.
 *
 * Takes the element rather than a ref: the chat's root element is replaced when a
 * signed-in session finishes loading and the page moves into the dashboard
 * layout, and an effect keyed on a ref object never saw that, so it went on
 * sizing the detached element while the visible one kept its CSS height.
 */
export function useChatViewport(
  page: HTMLElement | null,
  { enabled }: { enabled: boolean },
): void {
  useEffect(() => {
    if (!enabled) return;
    const viewport = window.visualViewport;
    if (!page || !viewport) return;

    const root = document.documentElement;
    root.classList.add("chat-viewport-locked");

    let frame = 0;
    const fit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (window.scrollY !== 0) window.scrollTo(0, 0);
        const top = page.getBoundingClientRect().top;
        const visibleBottom = viewport.offsetTop + viewport.height;
        page.style.height = `${Math.max(0, Math.round(visibleBottom - top))}px`;
      });
    };

    fit();
    viewport.addEventListener("resize", fit);
    viewport.addEventListener("scroll", fit);
    window.addEventListener("resize", fit);
    window.addEventListener("scroll", fit, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("resize", fit);
      viewport.removeEventListener("scroll", fit);
      window.removeEventListener("resize", fit);
      window.removeEventListener("scroll", fit);
      root.classList.remove("chat-viewport-locked");
      page.style.height = "";
    };
  }, [page, enabled]);
}
